import React, { useCallback, useState } from "react";
import {
  DataGridPremium,
  GridCellParams,
  GridColDef,
  GridColumnOrderChangeParams,
  GridPinnedColumnFields,
  GridToolbarExport,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import EditIcon from "@mui/icons-material/Edit";
import { IColumnVisibilityModel } from "../../../api/concierges/preferences";
import { IHomesite } from "../../../api/homesites/homesites.types";
import { Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { getDataGridRowStyles } from "../../../components/datagrid/datagridStyles"
import { HomeSitesDataIndex } from "../../../reactQuery/homeSitesDataIndex/types";
interface SelectedTab {
  title: string;
  width: number;
  value: number;
}
interface DataGridComponentProps {
  rows: HomeSitesDataIndex[];
  columns: GridColDef[];
  onCellClick: (params: GridCellParams) => void;
  columnVisibilityModel: {
    [key: string]: boolean;
  };
  columnOrder: string[];
  handleColumnVisibilityChange: (newModel: IColumnVisibilityModel) => void;
  selectedTab: SelectedTab;
  columnVisibilityModelTabs: {
    [key: string]: boolean;
  };
  handleColumnOrderChange: (params: GridColumnOrderChangeParams) => void;
  handleCellClick: (params: GridCellParams) => void;
  theme: Theme;
  pinnedColumns: GridPinnedColumnFields;
  onPinnedColumnsChange: (newPinned: GridPinnedColumnFields) => void;
}

const DataGridPremiumComponent: React.FC<DataGridComponentProps> = ({
  rows,
  columns,
  columnVisibilityModel,
  handleColumnVisibilityChange,
  selectedTab,
  columnVisibilityModelTabs,
  handleColumnOrderChange,
  handleCellClick,
  theme,
  pinnedColumns,
  onPinnedColumnsChange
}) => {
  const apiRef = useGridApiRef();

  const CustomToolbar = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "8px",
        }}
      >
        <GridToolbarContainer>
          {selectedTab.value === 0 && (
            <GridToolbarColumnsButton
              slotProps={{
                button: {
                  variant: "contained",
                  style: { textTransform: "none" },
                },
              }}
            />
          )}
          <GridToolbarFilterButton
            slotProps={{
              button: {
                variant: "contained",
                style: { textTransform: "none" },
              },
            }}
          />
          <GridToolbarExport
            slotProps={{
              button: {
                variant: "contained",
                style: { textTransform: "none" },
              },
            }}
            printOptions={{ disableToolbarButton: true }}
          />
        </GridToolbarContainer>
        <div style={{ display: "flex", alignItems: "center" }}>
          <EditIcon style={{ color: "#005DAA", fontSize: "16px", marginRight: "4px" }} />
          <span>Editable fields</span>
        </div>
      </div>
    );
  };

  // forward changes to the parent
  const handlePinnedChange = useCallback(
    (newPinned: GridPinnedColumnFields) => {
      onPinnedColumnsChange(newPinned);
    },
    [onPinnedColumnsChange]
  );

  return (
    <div style={{ height: "70vh", width: "100%" }}>
      <DataGridPremium
        rows={rows}
        rowHeight={35} /* By default row height is 52px */
        columns={columns}
        apiRef={apiRef}
        columnVisibilityModel={
          selectedTab.value === 0
            ? columnVisibilityModel
            : columnVisibilityModelTabs
        }
        onColumnVisibilityModelChange={
          selectedTab.value === 0 ? handleColumnVisibilityChange : undefined
        }
        onColumnOrderChange={
          selectedTab.value === 0 ? handleColumnOrderChange : undefined
        }
        pagination
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={handlePinnedChange}
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
        }}
        disableAggregation
        disableRowGrouping
        disableColumnPinning={false}
        sx={{
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
          "& .MuiDataGrid-columnSeparator": {
            visibility: "visible",
            color: "divider",
          },
          "& .MuiDataGrid-cell": {
            borderRight: "1px solid rgba(224, 224, 224, 1)",
          },
          "& .super-app-theme--header": {
            backgroundColor: "#DCE3E8",
          },
          "& .MuiDataGrid-cell--textLeft[data-field='foreignNationalLoan']": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          "& .MuiDataGrid-cell[data-field='milestoneProgress']": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 !important",
          },
          ...getDataGridRowStyles(theme, true),
          "& .MuiDataGrid-row .MuiDataGrid-cell.ecoe-mismatch-cell": {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
          },
        }}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{ toolbar: { selectedTab: selectedTab } }}
        onCellClick={handleCellClick}
        getRowClassName={(params) => {
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
      />
    </div>
  );
};

export default DataGridPremiumComponent;